/**
 * React Query hooks for deployment operations.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { deploymentsApi } from '@/services/deployments'
import type {
  DeploymentRequest,
  DeploymentLog,
  DeploymentStage,
  CreateDeploymentRequest,
  DeploymentFilters,
  PipelineRun,
  AppriseVmDetail,
} from '@/types'

export const deploymentKeys = {
  all: ['deployments'] as const,
  lists: () => [...deploymentKeys.all, 'list'] as const,
  list: (filters?: DeploymentFilters) =>
    [...deploymentKeys.lists(), filters] as const,
  details: () => [...deploymentKeys.all, 'detail'] as const,
  detail: (id: number) => [...deploymentKeys.details(), id] as const,
  logs: (id: number) => [...deploymentKeys.all, 'logs', id] as const,
  appriseVm: (id: number) => [...deploymentKeys.all, 'apprise-vm', id] as const,
}

export function useDeployments(filters?: DeploymentFilters) {
  return useQuery({
    queryKey: deploymentKeys.list(filters),
    queryFn: async () => {
      const res = await deploymentsApi.list(filters)
      return res.data
    },
  })
}

export function useDeployment(
  id: number,
  options?: Partial<UseQueryOptions<DeploymentRequest>>
) {
  return useQuery({
    queryKey: deploymentKeys.detail(id),
    queryFn: async () => {
      const res = await deploymentsApi.get(id)
      return res.data
    },
    enabled: !!id,
    ...options,
  })
}

export function useDeploymentLogs(
  id: number,
  options?: Partial<UseQueryOptions<DeploymentLog[]>>
) {
  return useQuery({
    queryKey: deploymentKeys.logs(id),
    queryFn: async () => {
      const res = await deploymentsApi.getLogs(id)
      return res.data
    },
    enabled: !!id,
    ...options,
  })
}

export function useCreateDeployment() {
  const queryClient = useQueryClient()
  return useMutation({
    // The request may carry the write-only Apprise `admin_password`: do not keep the variables in the
    // mutation cache once the page is gone.
    gcTime: 0,
    mutationFn: async (data: CreateDeploymentRequest) => {
      const res = await deploymentsApi.create(data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deploymentKeys.lists() })
    },
  })
}

export function useTriggerDeployment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await deploymentsApi.trigger(id)
      return res.data
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: deploymentKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: deploymentKeys.lists() })
    },
  })
}

export function useCancelDeployment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await deploymentsApi.cancel(id)
      return res.data
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: deploymentKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: deploymentKeys.lists() })
    },
  })
}

export function useDeploymentStages(deploymentId: number) {
  return useQuery<DeploymentStage[]>({
    queryKey: ['deployments', 'stages', deploymentId],
    queryFn: async () => {
      const res = await deploymentsApi.getStages(deploymentId)
      return res.data
    },
    enabled: !!deploymentId,
    refetchInterval: 3000,
  })
}

export function usePipelineRun(
  deploymentId: number,
  enabled: boolean,
  keepPollingAfterComplete = false,
) {
  return useQuery<PipelineRun>({
    queryKey: ['deployments', 'pipeline-run', deploymentId],
    queryFn: async () => {
      const res = await deploymentsApi.getPipelineRun(deploymentId)
      return res.data
    },
    enabled: !!deploymentId && enabled,
    // Poll the real GitHub Actions run every 5s. Keep polling when data is absent
    // (run not yet readable / transient error) so it recovers. For Ross, keep
    // polling even after infra completes (keepPollingAfterComplete) until the app
    // run URL appears — this endpoint chains the ADO app deploy as a fallback when
    // the GitHub webhook never fires, and needs repeated chances to succeed.
    refetchInterval: (query) => {
      if (query.state.data?.status !== 'completed') return 5000
      return keepPollingAfterComplete ? 5000 : false
    },
    retry: false,
  })
}

export function useAppPipelineRun(deploymentId: number, enabled: boolean) {
  return useQuery<PipelineRun>({
    queryKey: ['deployments', 'app-pipeline-run', deploymentId],
    queryFn: async () => {
      const res = await deploymentsApi.getAppPipelineRun(deploymentId)
      return res.data
    },
    enabled: !!deploymentId && enabled,
    // Poll the real Azure DevOps app-deploy run every 5s until it completes.
    refetchInterval: (query) => (query.state.data?.status === 'completed' ? false : 5000),
    retry: false,
  })
}

export function useSwitchTraffic() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, notes }: { id: number; notes?: string }) =>
      deploymentsApi.switchTraffic(id, notes),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: deploymentKeys.detail(id) })
    },
  })
}

export function useTriggerAutoFix() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, stageId }: { id: number; stageId: number }) =>
      deploymentsApi.triggerAutoFix(id, stageId),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['deployments', 'stages', id] })
    },
  })
}

// ─── Apprise-on-VM: plan history, server outputs, verify block and tracks ─────

/** Polls while `poll` is true (plan / apply / verify in flight). */
export function useAppriseVmDetail(deploymentId: number, enabled: boolean, poll: boolean) {
  return useQuery<AppriseVmDetail>({
    queryKey: deploymentKeys.appriseVm(deploymentId),
    queryFn: async () => {
      const res = await deploymentsApi.getAppriseVm(deploymentId)
      return res.data
    },
    enabled: !!deploymentId && enabled,
    refetchInterval: poll ? 5000 : false,
    retry: 1,
  })
}

function usePlanDecision(kind: 'approve' | 'reject') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, notes }: { id: number; notes: string }) => {
      const res = kind === 'approve'
        ? await deploymentsApi.approvePlan(id, notes)
        : await deploymentsApi.rejectPlan(id, notes)
      return res.data
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: deploymentKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: deploymentKeys.appriseVm(id) })
      queryClient.invalidateQueries({ queryKey: deploymentKeys.lists() })
    },
  })
}

export function useApprovePlan() {
  return usePlanDecision('approve')
}

export function useRejectPlan() {
  return usePlanDecision('reject')
}
